import type {
  LocalOnlineCourse,
  LocalOnlineCourseListItem,
} from "@/lib/online-courses-shared";
import * as Sentry from "@sentry/nextjs";

type TrackableOnlineCourse = LocalOnlineCourse | LocalOnlineCourseListItem;

const titleCase = (value: string) =>
  value
    .replace(/-/g, " ")
    .replace(/(^\w{1})|(\s{1}\w{1})/g, (match) => match.toUpperCase());

export type OnlineCourseAnalyticsItem = {
  item_id: string;
  item_name: string;
  affiliation: string;
  currency: string;
  item_brand: string;
  item_category: string;
  item_category2?: string;
  item_list_id?: string;
  item_list_name?: string;
  price: number;
  quantity: number;
  configuration_id?: string;
  option_code?: string;
};

const captureTrackingException = (
  error: unknown,
  eventName: string,
  extra: Record<string, string | number | boolean | undefined> = {}
) => {
  Sentry.captureException(error, {
    tags: {
      area: "online_course_tracking",
      tracking_event: eventName,
      ...extra,
    },
  });
};

const runTracking = (
  eventName: string,
  callback: () => void,
  extra: Record<string, string | number | boolean | undefined> = {}
) => {
  try {
    callback();
  } catch (error) {
    captureTrackingException(error, eventName, extra);
  }
};

export const buildOnlineCourseAnalyticsItem = (
  course: TrackableOnlineCourse,
  extras: { configurationId?: string; optionCode?: string } = {},
): OnlineCourseAnalyticsItem => ({
  item_id: extras.configurationId || course.price_id || course.slug,
  item_name: course.title,
  affiliation: "Cosmetic College",
  currency: "GBP",
  item_brand: "Cosmetic College",
  item_category: "Online Training Courses",
  item_category2: course.hub_category,
  item_list_id: course.hub_category,
  item_list_name: titleCase(course.hub_category),
  price: course.price_pence / 100,
  quantity: 1,
  configuration_id: extras.configurationId,
  option_code: extras.optionCode,
});

export const trackOnlineEvent = (
  name: string,
  properties: Record<string, any> = {}
) => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }
  runTracking(name, () => {
    window.gtag("event", name, properties);
  });
};

export const trackOnlineAddToCart = (
  course: TrackableOnlineCourse,
  source: string,
  extras: { configurationId?: string; optionCode?: string } = {},
) => {
  if (typeof window === "undefined") {
    return;
  }

  const item = buildOnlineCourseAnalyticsItem(course, extras);
  const value = course.price_pence / 100;

  runTracking("add_to_cart", () => {
    if (window.gtag) {
      window.gtag("event", "add_to_cart", {
        currency: "GBP",
        value,
        items: [item],
        checkout_source: source,
        configuration_id: extras.configurationId,
        option_code: extras.optionCode,
      });
    }
  }, { source, configuration_id: extras.configurationId, option_code: extras.optionCode });

  runTracking("meta_add_to_cart", () => {
    // @ts-ignore
    if (window.fbq) {
      // @ts-ignore
      window.fbq("track", "AddToCart", {
        content_ids: [item.item_id],
        content_name: item.item_name,
        content_type: "product",
        contents: [{ id: item.item_id, quantity: item.quantity }],
        currency: "GBP",
        value,
      });
    }
  }, { source });

};

export const trackOnlineBeginCheckout = ({
  items,
  valuePence,
  source,
  discountPence = 0,
}: {
  items: OnlineCourseAnalyticsItem[];
  valuePence: number;
  source: string;
  discountPence?: number;
}) => {
  if (typeof window === "undefined") {
    return;
  }

  const value = valuePence / 100;
  const discount = discountPence / 100;
  const contentIds = items.map((item) => item.item_id);

  runTracking("begin_checkout", () => {
    if (window.gtag) {
      window.gtag("event", "begin_checkout", {
        currency: "GBP",
        value,
        discount,
        items,
        checkout_source: source,
      });
    }
  }, { source });

  runTracking("meta_initiate_checkout", () => {
    // @ts-ignore
    if (window.fbq) {
      // @ts-ignore
      window.fbq("track", "InitiateCheckout", {
        content_ids: contentIds,
        content_type: "product",
        contents: items.map((item) => ({
          id: item.item_id,
          quantity: item.quantity,
        })),
        currency: "GBP",
        num_items: items.reduce((total, item) => total + item.quantity, 0),
        value,
      });
    }
  }, { source });

};

export const trackLocalOnlineHubView = (courses: LocalOnlineCourseListItem[]) => {
  if (typeof window === "undefined") {
    return;
  }

  runTracking("view_item_list", () => {
    if (window.gtag) {
      window.gtag("event", "view_item_list", {
        item_list_id: "online-training-courses",
        item_list_name: "Online Training Courses",
        items: courses.map((course) => buildOnlineCourseAnalyticsItem(course)),
      });
    }
  });
};

export const trackLocalOnlineCourseView = (course: LocalOnlineCourse) => {
  if (typeof window === "undefined") {
    return;
  }

  runTracking("view_item", () => {
    if (window.gtag) {
      window.gtag("event", "view_item", {
        currency: "GBP",
        value: course.price_pence / 100,
        items: [buildOnlineCourseAnalyticsItem(course)],
      });
    }
  }, { course_slug: course.slug });

  try {
    // @ts-ignore
    if (window.fbq) {
      // @ts-ignore
      window.fbq("track", "ViewContent", {
        value: course.price_pence / 100,
        currency: "GBP",
        content_name: course.title,
        content_type: "product",
        content_ids: course.price_id || course.slug,
      });
    }
  } catch (error) {
    captureTrackingException(error, "meta_view_content", {
      course_slug: course.slug,
    });
  }
};

export const trackOnlineFunnelStep = ({
  step,
  configurationId,
  optionCode,
  registrationId,
  dedupeKey,
}: {
  step:
    | 'purchased'
    | 'theory_complete'
    | 'model_report_complete'
    | 'certified';
  configurationId?: string;
  optionCode?: string;
  registrationId?: string;
  dedupeKey?: string;
}) => {
  if (typeof window !== "undefined" && dedupeKey) {
    try {
      if (sessionStorage.getItem(dedupeKey)) {
        return;
      }
      sessionStorage.setItem(dedupeKey, "1");
    } catch {
      // sessionStorage may be unavailable
    }
  }

  trackOnlineEvent(`online_funnel_${step}`, {
    configuration_id: configurationId,
    option_code: optionCode,
    online_registration_id: registrationId,
    funnel_step: step,
  });
};

export const trackLocalOnlineCardClick = (
  course: LocalOnlineCourseListItem,
  location: string
) => {
  trackOnlineEvent("online_course_card_click", {
    course_slug: course.slug,
    course_title: course.title,
    course_category: course.hub_category,
    qualification_type: course.qualification_type,
    delivery_mode: "online",
    price: course.price_pence / 100,
    location,
  });
};
